import type { SessionSnapshot } from '@scrambl/shared/session-scope'

export interface OAuthProof {
  version: 1
  id: string
  verifier: string
  challenge: string
  state: string | null
  expiresAt: number
  identity: string | null
  ownerKey?: string | null
}
const LIFETIME = 600000
export const OAUTH_PROOF_KEY = 'oauth_client_proof_v1'

/** The public URL never contains the private verifier. One attempt owns each client. */
export function createOAuthProofOwner(options: {
  read(): Promise<unknown>
  write(value: OAuthProof | null, expectedId?: string): Promise<void>
  createProof(): Promise<{ verifier: string; challenge: string }>
  randomUUID(): string
  capture(): SessionSnapshot
  persistentOwner?(): string | null
  now?(): number
}) {
  const now = options.now ?? Date.now
  const sessions = new Map<string, SessionSnapshot>()
  let processIntentId: string | null = null
  let processEpoch = 0
  let epoch = 0
  let currentState: string | null = null
  let tail = Promise.resolve()
  function serial<T>(work: () => Promise<T>) {
    const result = tail.then(work)
    tail = result.then(
      () => {},
      () => {},
    )
    return result
  }
  async function read(): Promise<OAuthProof> {
    const value = (await options.read()) as OAuthProof | null
    if (
      !value ||
      value.version !== 1 ||
      typeof value.id !== 'string' ||
      !value.id ||
      typeof value.verifier !== 'string' ||
      !/^[A-Za-z0-9._~-]{43,128}$/.test(value.verifier) ||
      typeof value.challenge !== 'string' ||
      !/^[A-Za-z0-9_-]{43}$/.test(value.challenge) ||
      (value.state !== null &&
        (typeof value.state !== 'string' ||
          !value.state ||
          value.state.length > 4096)) ||
      (value.identity !== null && typeof value.identity !== 'string') ||
      (value.ownerKey !== undefined &&
        value.ownerKey !== null &&
        (typeof value.ownerKey !== 'string' ||
          !value.ownerKey ||
          value.ownerKey.length > 2048)) ||
      !Number.isFinite(value.expiresAt) ||
      value.expiresAt <= now() ||
      value.expiresAt > now() + LIFETIME
    )
      throw new Error('This sign-in attempt expired. Start signing in again.')
    return value
  }
  function assertOwner(value: OAuthProof) {
    if (epoch > 0 && (value.id !== processIntentId || processEpoch !== epoch))
      throw new Error('This sign-in attempt is no longer active.')
    const session = sessions.get(value.id) ?? options.capture()
    session.assertCurrent()
    if (options.persistentOwner && value.ownerKey !== options.persistentOwner())
      throw new Error('The active sign-in changed. Start signing in again.')
    if (session.identity !== value.identity)
      throw new Error('The active account changed. Start signing in again.')
    sessions.set(value.id, session)
    return session
  }
  return {
    prepare() {
      // Capture before crypto/storage/network work, rather than when the response arrives.
      const session = options.capture()
      const ownerKey = options.persistentOwner?.()
      const mine = ++epoch
      return serial(async () => {
        session.assertCurrent()
        const proof = await options.createProof()
        if (mine !== epoch)
          throw new Error('This sign-in attempt is no longer active.')
        session.assertCurrent()
        const value: OAuthProof = {
          ...proof,
          version: 1,
          id: options.randomUUID(),
          state: null,
          expiresAt: now() + LIFETIME,
          identity: session.identity,
          ...(options.persistentOwner ? { ownerKey } : {}),
        }
        await options.write(value)
        processIntentId = value.id
        processEpoch = mine
        if (mine !== epoch)
          throw new Error('This sign-in attempt is no longer active.')
        session.assertCurrent()
        sessions.clear()
        currentState = null
        sessions.set(value.id, session)
        return value
      })
    },
    bind(id: string, authUrl: string) {
      return serial(async () => {
        const value = await read()
        assertOwner(value)
        const url = new URL(authUrl)
        const state = url.searchParams.get('state')
        if (
          value.id !== id ||
          !state ||
          state.length > 4096 ||
          url.searchParams.get('code_challenge') !== value.challenge ||
          url.searchParams.get('code_challenge_method') !== 'S256'
        )
          throw new Error(
            'The sign-in request changed. Start signing in again.',
          )
        await options.write({ ...value, state }, value.id)
        currentState = state
      })
    },
    exchange<T>(
      state: string,
      work: (verifier: string, session: SessionSnapshot) => Promise<T>,
    ) {
      const mine = epoch
      return serial(async () => {
        const value = await read()
        const session = assertOwner(value)
        if (!state || value.state !== state)
          throw new Error('This callback belongs to another sign-in attempt.')
        const result = await work(value.verifier, session)
        if (mine !== epoch)
          throw new Error('This sign-in attempt is no longer active.')
        session.assertCurrent()
        // Other windows use the native vault revision; another tab has its own receipt.
        const current = await read()
        assertOwner(current)
        if (current.id !== value.id || current.state !== state)
          throw new Error('This sign-in attempt is no longer active.')
        await options.write(null, value.id)
        session.assertCurrent()
        sessions.delete(value.id)
        return { result, session }
      })
    },
    cancel(id: string) {
      if (sessions.has(id) && processIntentId === id && processEpoch === epoch)
        ++epoch
      return serial(async () => {
        const value = (await options.read()) as OAuthProof | null
        if (value?.id === id) await options.write(null, value.id)
        sessions.delete(id)
      })
    },
    cancelState(state: string) {
      if (currentState === state && processEpoch === epoch) ++epoch
      return serial(async () => {
        const value = (await options.read()) as OAuthProof | null
        if (value?.state !== state) return
        await options.write(null, value.id)
        sessions.delete(value.id)
        if (currentState === state) currentState = null
      })
    },
  }
}

export async function browserOAuthProof(): Promise<{
  verifier: string
  challenge: string
}> {
  const bytes = crypto.getRandomValues(new Uint8Array(32))
  const verifier = Array.from(bytes, value =>
    value.toString(16).padStart(2, '0'),
  ).join('')
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier),
  )
  const challenge = btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
  return { verifier, challenge }
}
